/**
 * Konverzia plain-textového tela emailu na čitateľné HTML.
 *
 * Textová alternatíva HTML emailu (Gmail/Outlook) zapisuje hyperlinky
 * konvenciou `label <URL>` — kým sa zobrazuje HTML part, používateľ vidí
 * pekný link; keď sa niekde v reťazci (forward cez text-only klienta) HTML
 * stratí, ostane text plný `<https://www.google.com/maps/...>` balastu.
 *
 * textEmailToHtml() tieto echá zbalí späť na linky:
 *  - `Kukučínova 1 <https://maps…>`  → `<a href="https://maps…">Kukučínova 1</a>`
 *  - label na riadku a `<URL>` na ďalšom riadku → label sa stane linkom,
 *    riadok s echom sa zahodí
 *  - `<URL>` bez labelu → viditeľný klikateľný link (bez lomených zátvoriek)
 *  - holé URL v texte sa linkifikujú
 *
 * `Meno <adresa@domena>` (mailová konvencia hlavičiek) sa NEdotýka — regex
 * matchuje výhradne http(s) URL, takže `javascript:`/`data:` schémy sa do
 * href nikdy nedostanú. Všetok text je escapovaný.
 */

import { decodeHTML } from 'entities'

const URL_ECHO_RE = /<(https?:\/\/[^\s<>]+)>/g
const PURE_ECHO_RE = /^\s*<(https?:\/\/[^\s<>]+)>\s*$/
const BARE_URL_RE = /https?:\/\/[^\s<>"']+/g

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function anchor(url: string, label: string): string {
  return `<a href="${escapeHtml(url)}">${escapeHtml(label)}</a>`
}

/** Escapuje segment textu a linkifikuje holé URL v ňom. */
function plainSegmentToHtml(segment: string): string {
  let out = ''
  let last = 0
  for (const m of segment.matchAll(BARE_URL_RE)) {
    out += escapeHtml(segment.slice(last, m.index))
    out += anchor(m[0], m[0])
    last = m.index + m[0].length
  }
  return out + escapeHtml(segment.slice(last))
}

/**
 * Prevedie plain-textové telo emailu na bezpečné HTML (escapované, linky len
 * http/https). Výstup je jeden `<div>` so zachovanými riadkami (pre-wrap).
 */
export function textEmailToHtml(text: string): string {
  const lines = text.replace(/\r\n/g, '\n').split('\n')
  // plainLabel: pôvodný text riadku, ak je čistý text — kandidát na label
  // pre `<URL>` echo na nasledujúcom riadku.
  const out: Array<{ html: string; plainLabel: string | null }> = []

  for (const line of lines) {
    const pure = line.match(PURE_ECHO_RE)
    if (pure) {
      const url = pure[1]!
      const prev = out[out.length - 1]
      if (prev?.plainLabel?.trim()) {
        // Label je predchádzajúci riadok — stane sa linkom, echo riadok padá.
        prev.html = anchor(url, prev.plainLabel.trim())
        prev.plainLabel = null
        continue
      }
      out.push({ html: anchor(url, url), plainLabel: null })
      continue
    }

    if (!URL_ECHO_RE.test(line)) {
      out.push({ html: plainSegmentToHtml(line), plainLabel: line })
      continue
    }
    URL_ECHO_RE.lastIndex = 0

    // Inline echá: `… label <URL> …` — label = text od začiatku riadku
    // (resp. od predchádzajúceho echa) po `<`.
    let html = ''
    let last = 0
    for (const m of line.matchAll(URL_ECHO_RE)) {
      const before = line.slice(last, m.index)
      const url = m[1]!
      const label = before.trim()
      if (label) {
        const leadWs = before.match(/^\s*/)![0]
        html += escapeHtml(leadWs) + anchor(url, before.trim())
      } else {
        html += escapeHtml(before) + anchor(url, url)
      }
      last = m.index + m[0].length
    }
    html += plainSegmentToHtml(line.slice(last))
    out.push({ html, plainLabel: null })
  }

  // Riadky spája <br>, nie \n: pre-wrap newliny nerešpektuje každý HTML parser
  // (TipTap v admin composeri ich zahodí a susedné rovnaké linky zlúči do
  // jedného). pre-wrap ostáva kvôli viacnásobným medzerám (ASCII zarovnanie).
  return `<div style="white-space:pre-wrap;word-break:break-word">${out.map((o) => o.html).join('<br>')}</div>`
}

/**
 * Dekóduje HTML entity v texte (pomenované — celá HTML 5 sada, číselné dec aj
 * hex) cez `entities` (fb55, jadro htmlparser2/cheerio; čistý JS bez DOM, beží
 * v RN aj v API). Lokálne dekodéry poznali len nbsp/amp/lt/gt — citácia
 * preposlaného webmailového mailu (Roundcube kóduje diakritiku ako
 * &aacute;/&scaron;) bola plná entít (iOS, 2026-09-21). Neznáme entity
 * ostávajú tak, ako sú; `&amp;scaron;` dá doslovné `&scaron;` (jeden prechod).
 * Pozor: runtime dependency v @repo/types = po zmene rebuild dev images
 * (node_modules sú z image, pnpm nehoistuje).
 */
export function decodeHtmlEntities(text: string): string {
  return decodeHTML(text)
}

/**
 * Sploští HTML e-mailu na čitateľný text (citácia v mobilnom compose,
 * náhľad pre AI asistenta, fallback bez textového partu). Zahodí
 * style/script/head, blokové značky prevedie na nové riadky, dekóduje
 * všetky entity a zlúči viacnásobné prázdne riadky. Bez DOM.
 */
export function htmlToPlainText(html: string): string {
  const text = html
    .replace(/<(style|script|head|title)\b[^>]*>[\s\S]*?<\/\1>/gi, '')
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<br\s*\/?>/gi, '\n')
    // Blokový prvok = prázdny riadok (odseky mailu), <br> = nový riadok.
    .replace(/<\/(p|div|tr|li|h[1-6]|blockquote)>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
  return decodeHtmlEntities(text)
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}
